import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@/shared/utils";
import { useGetBankAccountsQuery, useGetBankStatementsQuery } from "../api";
import type { TransactionType } from "../types";

const MAX_RANGE_DAYS = 92;

function formatDateParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getDefaultDateRange(): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from, to };
}

export function useBankingPage() {
  const { i18n } = useTranslation();
  const locale = i18n.language as Locale;

  const {
    data: accounts,
    isLoading: isAccountsLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useGetBankAccountsQuery();

  const [selectedIban, setSelectedIban] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TransactionType>("ALL");

  useEffect(function autoSelectFirstAccount() {
    if (accounts && accounts.length > 0 && !selectedIban) {
      setSelectedIban(accounts[0].iban);
    }
  }, [accounts, selectedIban]);

  const selectedAccount = useMemo(
    () => accounts?.find((a) => a.iban === selectedIban) ?? null,
    [accounts, selectedIban],
  );

  const statementParams = useMemo(() => {
    if (!selectedIban) return null;
    return {
      iban: selectedIban,
      date_from: formatDateParam(dateRange.from),
      date_to: formatDateParam(dateRange.to),
    };
  }, [selectedIban, dateRange]);

  const {
    data: statement,
    isLoading: isStatementLoading,
    isFetching: isStatementFetching,
    error: statementError,
  } = useGetBankStatementsQuery(statementParams!, { skip: !statementParams });

  const handleDateRangeChange = useCallback(function handleDateRangeChange(from: Date, to: Date) {
    const diffMs = to.getTime() - from.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > MAX_RANGE_DAYS) {
      const clampedFrom = new Date(to);
      clampedFrom.setDate(clampedFrom.getDate() - MAX_RANGE_DAYS);
      setDateRange({ from: clampedFrom, to });
    } else {
      setDateRange({ from, to });
    }
  }, []);

  const filteredTransactions = useMemo(() => {
    if (!statement?.transactions) return [];
    let filtered = statement.transactions;

    if (typeFilter !== "ALL") {
      filtered = filtered.filter((t) => t.type === typeFilter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.description.toLowerCase().includes(q) ||
          t.correspondentName.toLowerCase().includes(q),
      );
    }

    return filtered;
  }, [statement?.transactions, typeFilter, search]);

  const isLoading = isAccountsLoading || (isStatementLoading && !!statementParams);
  const isFetching = isStatementFetching;

  const error = accountsError || statementError;
  const isServiceError = error && "status" in error && (error.status === 502 || error.status === 503);

  return {
    accounts: accounts ?? [],
    selectedIban,
    setSelectedIban,
    selectedAccount,
    statement,
    dateRange,
    handleDateRangeChange,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    filteredTransactions,
    isLoading,
    isFetching,
    error,
    isServiceError,
    refetchAccounts,
    locale,
  };
}
